import { createSelector } from '@reduxjs/toolkit'
import defaultsDeep from 'lodash/defaultsDeep'
import cloneDeep from 'lodash/cloneDeep'
import pickBy from 'lodash/pickBy'
import reduce from 'lodash/reduce'
import each from 'lodash/each'
import { selectEdgesForNode } from './edges'
import { selectUsers } from 'selectors/users'

type GraphNodeMap = import('types/domain').GraphNodeMap
type UserMap = import('types/domain').UserMap
type RootState = import('types/state').RootState

interface RouteProps {
  params?: {
    nodeId?: string
    deleted?: boolean
  }
}

interface NodeEntity {
  id: string
  deleted?: boolean
  data: {
    readers?: string[]
    writers?: string[]
    creator?: unknown
  }
}

interface EdgeEntity {
  data: {
    to?: string
    from?: string
  }
}


const selectNodes = (state: RootState): GraphNodeMap => {
  return state.graph?.nodes?.all || {}
}

const selectNodeId = (_state: RootState, props?: RouteProps): string | undefined => {
  return props?.params?.nodeId
}

const selectIncludeDeleted = (_state: RootState, props?: RouteProps): boolean => {
  return Boolean(props?.params?.deleted)
}

export const all = createSelector(selectNodes, selectIncludeDeleted, selectUsers, (nodes: GraphNodeMap, includeDeleted: boolean, users: UserMap) => {
  const filtered = pickBy(nodes, (n: NodeEntity) => includeDeleted || !n.deleted) as GraphNodeMap
  each(filtered, (n: NodeEntity, id: string) => {
    const updated = cloneDeep(n)
    updated.data.readers = reduce(
      n.data.readers ?? [],
      (rs: UserMap, rId: string) => {
        rs[rId] = defaultsDeep(users[rId], { id: rId })
        return rs
      },
      {} as UserMap
    )
    updated.data.writers = reduce(
      n.data.writers ?? [],
      (ws: UserMap, wId: string) => {
        ws[wId] = defaultsDeep(users[wId], { id: wId })
        return ws
      },
      {} as UserMap
    )
    const creatorId = n.data.creator as string | null | undefined
    const creatorUser = creatorId ? users[creatorId] : undefined
    updated.data.creator = defaultsDeep(creatorUser, { id: creatorId })
    filtered[id] = updated
  })
  return filtered
})

export const neighbors = createSelector(selectNodeId, all, selectEdgesForNode, (id: string | undefined, nodes: GraphNodeMap, edges: EdgeEntity[]) => {
  if (!id) {
    return []
  }
  return reduce(
    edges,
    (ns: string[], e: EdgeEntity) => {
      if (e.data.to && nodes[e.data.to] && e.data.to !== id) {
        ns.push(e.data.to)
      }
      if (e.data.from && nodes[e.data.from] && e.data.from !== id) {
        ns.push(e.data.from)
      }
      return ns
    },
    [] as string[]
  )
})

export default {
  all,
  neighbors
}
